import React, { useEffect, useRef, useState } from "react";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { ReactComponent as DocumentIcon } from "@assets/svg/document-filled.svg";
import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { Dialog, DialogContent } from "@components/ui/dialog";

import { MARKER_EXCLUDE_CLASS } from "@constants/marker";

import { stripBundlePrefix } from "@utils/bundle-name";

import { FilePreviewData } from "../types/data-table";

// Resolve the pdfjs worker locally from the installed package (Vite) — must
// match react-pdf's bundled pdfjs (4.8.69). The old 3.11.174 CDN URL here
// silently broke the text layer wherever this module loaded last (MDRS-134).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FilePreviewData | null;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  file,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [documentContent, setDocumentContent] = useState("");
  const [previewType, setPreviewType] = useState<
    "pdf" | "image" | "text" | "doc" | "unsupported"
  >("unsupported");
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset preview state between files.
  const resetState = () => {
    setDocumentContent("");
    setPreviewType("unsupported");
    setNumPages(0);
    setIsLoading(false);
    pageRefs.current = {};
  };

  useEffect(() => {
    if (!file || !isOpen) {
      resetState();
      return;
    }

    const processFile = () => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      setIsLoading(true);

      try {
        if (fileExtension === "pdf" || file.type === "pdf") {
          setPreviewType("pdf");
          setDocumentContent(file.url || "");
        } else if (
          ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(
            fileExtension || ""
          ) ||
          file.type?.startsWith("image/")
        ) {
          setPreviewType("image");
          setDocumentContent(file.url || "");
        } else if (
          ["doc", "docx"].includes(fileExtension || "") ||
          file.type === "doc"
        ) {
          setPreviewType("doc");
          setDocumentContent(file.url || "");
        } else if (
          ["txt", "md", "csv", "log"].includes(fileExtension || "") ||
          file.type === "text"
        ) {
          setPreviewType("text");
          setDocumentContent(file.content || "No content available.");
        } else {
          setPreviewType("unsupported");
        }
      } catch (_error) {
        setDocumentContent("Failed to load document content.");
      } finally {
        setIsLoading(false);
      }
    };

    processFile();
  }, [file, isOpen]);

  // Measure container width for PDF scaling
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerWidth(width);
      }
    };

    if (isOpen && previewType === "pdf") {
      // Initial measurement
      updateContainerWidth();
    }
  }, [isOpen, previewType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any blob URLs that might have been created
      if (documentContent && documentContent.startsWith("blob:")) {
        URL.revokeObjectURL(documentContent);
      }
    };
  }, [documentContent]);

  const renderPreviewContent = () => {
    if (isLoading) {
      return <PreviewLoader message="Loading document" />;
    }

    switch (previewType) {
      case "pdf":
        return documentContent ? (
          <div ref={containerRef} className="h-full w-full overflow-auto">
            <Document
              key={documentContent}
              file={documentContent}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(err) => console.error("PDF Load Error", err)}
              loading={<PreviewLoader message="Loading PDF document!" />}
            >
              {Array.from({ length: numPages }, (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  className="mb-4 flex w-full justify-center"
                  ref={(el) => {
                    pageRefs.current[index + 1] = el;
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={
                      containerWidth > 0 ? Math.min(containerWidth - 40, 800) : 600
                    } // Use container width minus padding, max 800px
                    loading={<PreviewLoader message={`Loading Page ${index + 1}`} />}
                  />
                </div>
              ))}
            </Document>
          </div>
        ) : (
          <EmptyState message="No PDF content available" />
        );

      case "image":
        return documentContent ? (
          <div className="flex h-full items-center justify-center rounded-lg">
            <img
              src={documentContent}
              alt={file?.name ? stripBundlePrefix(file.name) : ""}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <EmptyState message="No image content available" />
        );

      case "text":
        return (
          <div className="h-full overflow-auto rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-foreground-muted">
              {documentContent || "No text content available"}
            </pre>
          </div>
        );

      case "doc":
        return documentContent ? (
          <div className="h-full">
            <iframe
              src={documentContent}
              className="h-full w-full rounded-lg border"
              title={`Preview of ${file?.name ? stripBundlePrefix(file.name) : ""}`}
            />
          </div>
        ) : (
          <EmptyState message="No document content available" />
        );

      default:
        return (
          <UnsupportedFileState
            fileName={file?.name ? stripBundlePrefix(file.name) : undefined}
            fileType={previewType}
          />
        );
    }
  };

  if (!file) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="grid h-[80vh] w-[90vw] max-w-4xl grid-rows-[auto_1fr] p-0 md:min-w-[800px]">
        <h2 className="overflow-hidden text-ellipsis text-nowrap p-6 text-lg font-semibold">
          {stripBundlePrefix(file.name)}
        </h2>

        <div
          className={`flex h-full overflow-hidden px-6 pb-6 ${MARKER_EXCLUDE_CLASS}`}
        >
          {renderPreviewContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type PreviewLoaderProps = {
  message?: string;
};
const PreviewLoader = ({ message = "Loading" }: PreviewLoaderProps) => {
  return (
    <div className="flex h-full min-h-[400px] w-full items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-4">
        <LoadingIcon className="h-8 w-8 animate-spin-ccw" />
        <p className="animate-pulse text-sm text-foreground-muted">{message}</p>
      </div>
    </div>
  );
};

type EmptyStateProps = {
  message: string;
};
const EmptyState = ({ message }: EmptyStateProps) => {
  return (
    <div className="flex h-full items-center justify-center text-foreground-muted">
      <p>{message}</p>
    </div>
  );
};

type UnsupportedFileStateProps = {
  fileName?: string;
  fileType: string;
};
const UnsupportedFileState = ({ fileName, fileType }: UnsupportedFileStateProps) => {
  return (
    <div className="flex h-full flex-col items-center justify-center text-foreground-muted">
      <div className="mb-4">
        <DocumentIcon className="size-8" />
      </div>
      <h3 className="mb-2 text-lg font-medium">Preview not available</h3>
      <p className="mb-4 text-center text-sm">
        This file type cannot be previewed in the browser.
        <br />
        <span className="text-xs text-foreground-muted">
          File: {fileName} | Type: {fileType}
        </span>
      </p>
    </div>
  );
};
